interface CognitoAuthConfig {
  domainUrl: string;
  clientId: string;
  redirectUri: string;
  logoutUri: string;
  scopes: string[];
}

interface StoredSession {
  accessToken: string;
  idToken: string;
  refreshToken: string | null;
  tokenType: string;
  expiresAtMs: number;
}

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

interface JwtPayload {
  email?: string;
  "cognito:username"?: string;
  exp?: number;
}

export interface AuthUser {
  email: string | null;
  username: string | null;
}

const SESSION_STORAGE_KEY = "qpicks.auth.session.v1";
const PKCE_VERIFIER_KEY = "qpicks.auth.pkce.verifier";
const OAUTH_STATE_KEY = "qpicks.auth.pkce.state";

function normalizeBaseUrl(value: string | undefined): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim().replace(/\/+$/, "");
  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith("https://") || trimmed.startsWith("http://")) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

function getConfig(): CognitoAuthConfig | null {
  const domainUrl = normalizeBaseUrl(process.env.REACT_APP_COGNITO_DOMAIN);
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID?.trim() ?? "";

  if (!domainUrl || !clientId) {
    return null;
  }

  const fallbackOrigin = typeof window !== "undefined" ? window.location.origin : "http://localhost:3000";
  const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI?.trim() || `${fallbackOrigin}/auth/callback`;
  const logoutUri = process.env.REACT_APP_COGNITO_LOGOUT_URI?.trim() || `${fallbackOrigin}/signin`;
  const scopes = (process.env.REACT_APP_COGNITO_SCOPES ?? "openid email profile")
    .split(/\s+/)
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  return {
    domainUrl,
    clientId,
    redirectUri,
    logoutUri,
    scopes,
  };
}

function readStoredSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (
      typeof parsed.accessToken !== "string" ||
      typeof parsed.idToken !== "string" ||
      typeof parsed.tokenType !== "string" ||
      typeof parsed.expiresAtMs !== "number"
    ) {
      return null;
    }

    return {
      accessToken: parsed.accessToken,
      idToken: parsed.idToken,
      refreshToken: typeof parsed.refreshToken === "string" ? parsed.refreshToken : null,
      tokenType: parsed.tokenType,
      expiresAtMs: parsed.expiresAtMs,
    };
  } catch {
    return null;
  }
}

function storeSession(tokens: TokenResponse): void {
  const expiresAtMs = Date.now() + tokens.expires_in * 1000 - 15_000;
  const payload: StoredSession = {
    accessToken: tokens.access_token,
    idToken: tokens.id_token,
    refreshToken: tokens.refresh_token ?? null,
    tokenType: tokens.token_type,
    expiresAtMs,
  };

  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
}

function clearPkceTransientState(): void {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
}

export function clearAuthSession(): void {
  localStorage.removeItem(SESSION_STORAGE_KEY);
  clearPkceTransientState();
}

function encodeBase64Url(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((value) => {
    binary += String.fromCharCode(value);
  });

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function decodeBase64Url(value: string): string {
  let normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const mod = normalized.length % 4;
  if (mod > 0) {
    normalized += "=".repeat(4 - mod);
  }
  return atob(normalized);
}

function parseJwtPayload(idToken: string): JwtPayload | null {
  const parts = idToken.split(".");
  if (parts.length < 2) {
    return null;
  }

  try {
    const decoded = decodeBase64Url(parts[1]);
    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return null;
  }
}

function randomString(length: number): string {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const values = new Uint8Array(length);
  window.crypto.getRandomValues(values);
  return Array.from(values, (value) => alphabet[value % alphabet.length]).join("");
}

async function createCodeChallenge(verifier: string): Promise<string> {
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return encodeBase64Url(new Uint8Array(digest));
}

export function isAuthEnabled(): boolean {
  return getConfig() !== null || getCognitoApiConfig() !== null;
}

export function isAuthenticated(): boolean {
  const session = readStoredSession();
  if (!session) {
    return false;
  }

  if (Date.now() >= session.expiresAtMs) {
    clearAuthSession();
    return false;
  }

  return true;
}

export function getCurrentUser(): AuthUser | null {
  const session = readStoredSession();
  if (!session) {
    return null;
  }

  const payload = parseJwtPayload(session.idToken);
  if (!payload) {
    return null;
  }

  return {
    email: typeof payload.email === "string" ? payload.email : null,
    username: typeof payload["cognito:username"] === "string" ? payload["cognito:username"] : null,
  };
}

export async function beginHostedUiLogin(): Promise<void> {
  const config = getConfig();
  if (!config) {
    throw new Error("Cognito auth is not configured. Set REACT_APP_COGNITO_DOMAIN and REACT_APP_COGNITO_CLIENT_ID.");
  }

  const verifier = randomString(96);
  const state = randomString(48);
  const challenge = await createCodeChallenge(verifier);

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(OAUTH_STATE_KEY, state);

  const authorizeParams = new URLSearchParams({
    client_id: config.clientId,
    response_type: "code",
    redirect_uri: config.redirectUri,
    scope: config.scopes.join(" "),
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  window.location.assign(`${config.domainUrl}/oauth2/authorize?${authorizeParams.toString()}`);
}

export async function completeHostedUiLogin(currentUrl: string = window.location.href): Promise<void> {
  const config = getConfig();
  if (!config) {
    throw new Error("Cognito auth is not configured.");
  }

  const parsedUrl = new URL(currentUrl);
  const code = parsedUrl.searchParams.get("code");
  const callbackState = parsedUrl.searchParams.get("state");
  const error = parsedUrl.searchParams.get("error");
  const errorDescription = parsedUrl.searchParams.get("error_description");

  if (error) {
    throw new Error(errorDescription ? `${error}: ${errorDescription}` : error);
  }

  if (!code) {
    throw new Error("Missing authorization code in callback URL.");
  }

  const storedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  if (!storedState || !callbackState || storedState !== callbackState) {
    throw new Error("State validation failed for Cognito callback.");
  }

  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  if (!verifier) {
    throw new Error("PKCE verifier missing. Restart sign-in.");
  }

  const tokenBody = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.clientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  });

  const tokenResponse = await fetch(`${config.domainUrl}/oauth2/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: tokenBody.toString(),
  });

  if (!tokenResponse.ok) {
    const responseText = await tokenResponse.text();
    throw new Error(`Token exchange failed (${tokenResponse.status}): ${responseText}`);
  }

  const tokens = (await tokenResponse.json()) as TokenResponse;
  if (!tokens.access_token || !tokens.id_token || !tokens.expires_in || !tokens.token_type) {
    throw new Error("Cognito token response was missing required fields.");
  }

  storeSession(tokens);
  clearPkceTransientState();
}

export function beginHostedUiLogout(): void {
  const config = getConfig();
  clearAuthSession();

  if (!config) {
    return;
  }

  const logoutParams = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.logoutUri,
  });

  window.location.assign(`${config.domainUrl}/logout?${logoutParams.toString()}`);
}

/* ═══════════════════════════════════════════════════════════════════
   Direct Cognito User Pools API (replaces Hosted UI for sign-in)
   ═══════════════════════════════════════════════════════════════════ */

interface CognitoApiConfig {
  endpoint: string;
  clientId: string;
}

interface CognitoAuthResult {
  AccessToken: string;
  IdToken: string;
  RefreshToken?: string;
  TokenType: string;
  ExpiresIn: number;
}

export interface CognitoAuthResponse {
  ChallengeName?: string;
  ChallengeParameters?: Record<string, string>;
  Session?: string;
  AuthenticationResult?: CognitoAuthResult;
}

function getCognitoApiConfig(): CognitoApiConfig | null {
  const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID?.trim();
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID?.trim();
  if (!userPoolId || !clientId) return null;

  const region = userPoolId.split("_")[0];
  if (!region) return null;

  return {
    endpoint: `https://cognito-idp.${region}.amazonaws.com/`,
    clientId,
  };
}

export function isDirectAuthEnabled(): boolean {
  return getCognitoApiConfig() !== null;
}

async function cognitoApiCall(target: string, payload: Record<string, unknown>): Promise<unknown> {
  const apiConfig = getCognitoApiConfig();
  if (!apiConfig) throw new Error("Cognito direct auth is not configured. Set REACT_APP_COGNITO_USER_POOL_ID.");

  const response = await fetch(apiConfig.endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(payload),
  });

  const body = await response.json();
  if (!response.ok) {
    const errorType = (body as Record<string, string>).__type ?? "UnknownError";
    const errorMessage = (body as Record<string, string>).message ?? "Authentication failed.";
    const err = new Error(errorMessage);
    err.name = errorType;
    throw err;
  }

  return body;
}

function storeCognitoTokens(result: CognitoAuthResult): void {
  const tokens: TokenResponse = {
    access_token: result.AccessToken,
    id_token: result.IdToken,
    refresh_token: result.RefreshToken,
    token_type: result.TokenType,
    expires_in: result.ExpiresIn,
  };
  storeSession(tokens);
}

export async function signInWithCredentials(email: string, password: string): Promise<CognitoAuthResponse> {
  const apiConfig = getCognitoApiConfig();
  if (!apiConfig) throw new Error("Cognito direct auth is not configured.");

  const result = await cognitoApiCall("InitiateAuth", {
    AuthFlow: "USER_PASSWORD_AUTH",
    ClientId: apiConfig.clientId,
    AuthParameters: {
      USERNAME: email,
      PASSWORD: password,
    },
  }) as CognitoAuthResponse;

  if (result.AuthenticationResult) {
    storeCognitoTokens(result.AuthenticationResult);
  }

  return result;
}

export async function respondToNewPasswordChallenge(
  session: string,
  username: string,
  newPassword: string,
): Promise<CognitoAuthResponse> {
  const apiConfig = getCognitoApiConfig();
  if (!apiConfig) throw new Error("Cognito direct auth is not configured.");

  const result = await cognitoApiCall("RespondToAuthChallenge", {
    ChallengeName: "NEW_PASSWORD_REQUIRED",
    ClientId: apiConfig.clientId,
    Session: session,
    ChallengeResponses: {
      USERNAME: username,
      NEW_PASSWORD: newPassword,
    },
  }) as CognitoAuthResponse;

  if (result.AuthenticationResult) {
    storeCognitoTokens(result.AuthenticationResult);
  }

  return result;
}

export async function forgotPassword(email: string): Promise<void> {
  const apiConfig = getCognitoApiConfig();
  if (!apiConfig) throw new Error("Cognito direct auth is not configured.");

  await cognitoApiCall("ForgotPassword", {
    ClientId: apiConfig.clientId,
    Username: email,
  });
}

export async function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  const apiConfig = getCognitoApiConfig();
  if (!apiConfig) throw new Error("Cognito direct auth is not configured.");

  await cognitoApiCall("ConfirmForgotPassword", {
    ClientId: apiConfig.clientId,
    Username: email,
    ConfirmationCode: code,
    Password: newPassword,
  });
}

export function signOut(): void {
  clearAuthSession();
}
